/**
 * The Pendo event taxonomy. This is the only file that knows event names.
 *
 * Names come from CONTEXT.md — Calculate never run/solve/compute, Analyze is
 * Calculate + Design Review, Run is reserved for Overtime. Adding an event here
 * is adding a permanent contract: see the design spec before doing so.
 *
 * Properties must never carry Model names, Fluid Zone names, Device tags, project
 * names, filenames, checkpoint labels, prompt text, free-form errors or Element ids.
 */
import { useMemo } from 'react'
import { usePendo } from '@revalize/pendo/frontend'

/** The calculation status the store already derives; reused rather than re-invented. */
export type CalcOutcome = 'success' | 'partial' | 'invalid' | 'failed'

/** Auto-Size All's terminal status, as `autoSizeAllPipes` already returns it. */
export type AutoSizeStatus = 'converged' | 'max_iterations' | 'oscillation' | 'calc_failed'

/**
 * The `sourceType` values `Import Completed` is allowed to report.
 *
 * Deliberately narrower than `SourceType` (features/import/types/source-type.ts),
 * which is the full set ProjectSelectorDialog's `sourceType` prop can receive.
 * `revit-ifc`, `revit-schedule`, and `dxf` are hidden from UploadPage's picker
 * but their dispatch branches remain wired — if one of them is silently
 * re-enabled, it must NOT silently start emitting a new value into a live
 * taxonomy. `'manual'` is excluded for a different reason: it isn't an import
 * at all, and the emit site filters it before this type is ever consulted.
 *
 * Widening THIS union to add one of them back is caught at compile time
 * everywhere it's used (including the `@ts-expect-error` coverage in
 * persist.pendo.test.tsx). Widening `isTrackedImportSource`'s switch in
 * ProjectSelectorDialog.tsx to also return `true` for that value is NOT
 * caught — TypeScript does not check a hand-written type predicate against
 * what its body actually returns. See that function's comment: the two edits
 * must move together, and only this file's union is verified to enforce that.
 *
 * This asymmetry between the prop union and this event union is the point —
 * do not "fix" it by widening this type to match `SourceType`.
 */
export type TrackedImportSourceType =
  | 'pipe-flo'
  | 'json'
  | 'pipe-flo-desktop-json'
  | 'pipe-flo-docs'
  | 'aft-fathom'

export interface PendoEvents {
  'Model Calculated': { outcome: CalcOutcome; durationMs: number; pipeCount: number; vertexCount: number }
  // Deliberately the SAME shape as `Model Calculated` — Analyze is Calculate plus
  // Design Review (CONTEXT.md), so the two success rates have to stay comparable.
  // Both report what the invocation COMPUTED, not whether the apply-guard committed
  // it; a discarded solve therefore looks like a committed one on both. That caveat
  // is recorded in the instrumentation design and must be fixed on both events or
  // neither — an asymmetric `outcome` would silently make the rates incomparable.
  'Model Analyzed': { outcome: CalcOutcome; durationMs: number; pipeCount: number; vertexCount: number }
  'Pipes Auto-Sized': {
    pipesSized: number
    iterations: number
    compromises: number
    unresolvable: number
    status: AutoSizeStatus
  }
  'Report Exported': Record<string, never>
  'Import Completed': { sourceType: TrackedImportSourceType }
  'Model Version Created': Record<string, never>
  'Models Compared': Record<string, never>
  'Scenarios Compared': { scenarioCount: number }
  'FlowAssist Prompted': Record<string, never>
  'Overtime Run Started': Record<string, never>
}

export type PendoEventName = keyof PendoEvents

export type TrackEvent = <N extends PendoEventName>(name: N, props: PendoEvents[N]) => void

/**
 * Emit a typed product event.
 *
 * Deliberately does no error handling: PendoService already wraps every provider
 * call in try/catch and logs it, and PendoRoot's logger adapter is what makes that
 * logging reach anywhere. Duplicating it here would hide that.
 */
export function useTrackEvent(): TrackEvent {
  const pendo = usePendo()
  return useMemo((): TrackEvent => (name, props): void => { pendo.track(name, props) }, [pendo])
}
